import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Zone, FloorTable, TableSession } from '../../types';

interface TableState {
  zones: Zone[];
  tablesById: Record<number, FloorTable>;
  /** The open session for the table currently being viewed in the detail page. */
  currentSession: TableSession | null;
  /** Sessions for the local-orders ledger (the currently viewed day), kept live. */
  sessionsById: Record<number, TableSession>;
}

const initialState: TableState = {
  zones: [],
  tablesById: {},
  currentSession: null,
  sessionsById: {},
};

function sortZones(zones: Zone[]): Zone[] {
  return [...zones].sort((a, b) => (a.ordinal - b.ordinal) || (a.id - b.id));
}

const tableSlice = createSlice({
  name: 'table',
  initialState,
  reducers: {
    setFloorplan(state, action: PayloadAction<{ zones: Zone[]; tables: FloorTable[] }>) {
      state.zones = sortZones(action.payload.zones || []);
      state.tablesById = {};
      for (const t of action.payload.tables || []) state.tablesById[t.id] = t;
    },
    pushFloorplanTable(state, action: PayloadAction<FloorTable>) {
      state.tablesById[action.payload.id] = action.payload;
    },
    deleteFloorplanTable(state, action: PayloadAction<{ id: number }>) {
      delete state.tablesById[action.payload.id];
    },
    pushZone(state, action: PayloadAction<Zone>) {
      const idx = state.zones.findIndex((z) => z.id === action.payload.id);
      if (idx >= 0) state.zones[idx] = action.payload;
      else state.zones.push(action.payload);
      state.zones = sortZones(state.zones);
    },
    deleteZone(state, action: PayloadAction<{ id: number }>) {
      state.zones = state.zones.filter((z) => z.id !== action.payload.id);
      for (const id of Object.keys(state.tablesById)) {
        if (state.tablesById[Number(id)].zone_id === action.payload.id) {
          delete state.tablesById[Number(id)];
        }
      }
    },
    setCurrentSession(state, action: PayloadAction<TableSession | null>) {
      state.currentSession = action.payload;
    },
    pushTableSession(state, action: PayloadAction<TableSession>) {
      // Update the detail view if it's the session being viewed. Never touches
      // the local add-to-cart staging buffer (that lives in cartSlice).
      if (state.currentSession && state.currentSession.id === action.payload.id) {
        state.currentSession = action.payload;
      }
      // Keep the ledger live: upsert into the day map (the page filters by date).
      state.sessionsById[action.payload.id] = action.payload;
    },
    markSessionClosed(state, action: PayloadAction<{ session_id: number }>) {
      if (state.currentSession && state.currentSession.id === action.payload.session_id) {
        state.currentSession.status = 'closed';
      }
      const s = state.sessionsById[action.payload.session_id];
      if (s) s.status = 'closed';
    },
    clearCurrentSession(state) {
      state.currentSession = null;
    },
    setSessions(state, action: PayloadAction<TableSession[]>) {
      state.sessionsById = {};
      for (const s of action.payload) state.sessionsById[s.id] = s;
    },
  },
});

export const {
  setFloorplan, pushFloorplanTable, deleteFloorplanTable,
  pushZone, deleteZone, setCurrentSession, pushTableSession,
  markSessionClosed, clearCurrentSession, setSessions,
} = tableSlice.actions;

export default tableSlice.reducer;
